/* eslint-disable react-refresh/only-export-components -- DialogContentContainerContext travels with DimeDialogContent, which reads/writes it */
import * as React from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from './components/ui/dialog.js';
import { isEscapeForComboboxPopup } from './lib/is-escape-for-combobox-popup.js';
import { TOUCH_LAYOUT_CLASSES } from './lib/touch-layout-classes.js';
import { cn } from './lib/utils.js';

export function DimeDialog({ ...props }: Readonly<React.ComponentProps<typeof Dialog>>) {
  return <Dialog {...props} />;
}

export function DimeDialogTrigger({
  ...props
}: Readonly<React.ComponentProps<typeof DialogTrigger>>) {
  return <DialogTrigger {...props} />;
}

export function DimeDialogPortal({
  ...props
}: Readonly<React.ComponentProps<typeof DialogPortal>>) {
  return <DialogPortal {...props} />;
}

export function DimeDialogOverlay({
  ...props
}: Readonly<React.ComponentProps<typeof DialogOverlay>>) {
  return <DialogOverlay {...props} />;
}

export function DimeDialogClose({
  onMouseDown,
  ...props
}: Readonly<React.ComponentProps<typeof DialogClose>>) {
  return (
    <DialogClose
      {...props}
      onMouseDown={(event) => {
        onMouseDown?.(event);
        // Keeps focus in the form, so a blur-validation error can't shift the button out from under the click.
        event.preventDefault();
      }}
    />
  );
}

// Radix's Dialog locks page scroll via react-remove-scroll, which only exempts its own content node from the wheel-block, so a popover portaled elsewhere (e.g. Combobox) can't be scrolled while a modal Dialog is open.
export const DialogContentContainerContext = React.createContext<HTMLDivElement | null>(null);

export function DimeDialogContent({
  children,
  ref,
  onEscapeKeyDown,
  fullScreenOnPhone = false,
  className,
  ...props
}: Readonly<
  React.ComponentProps<typeof DialogContent> & {
    /** Fills a phone's screen, with a scrolling body between the header and footer, for touch work like picking. */
    fullScreenOnPhone?: boolean;
  }
>) {
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentEl(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  return (
    <DialogContent
      {...props}
      className={cn(fullScreenOnPhone && TOUCH_LAYOUT_CLASSES.phoneFullScreenDialog, className)}
      ref={mergedRef}
      onEscapeKeyDown={(event) => {
        // Radix sees Escape first, so without this an open combobox list, or a control in its popup, would close the whole dialog.
        if (isEscapeForComboboxPopup(event.target)) {
          event.preventDefault();
          return;
        }
        onEscapeKeyDown?.(event);
      }}
    >
      <DialogContentContainerContext.Provider value={contentEl}>
        {children}
      </DialogContentContainerContext.Provider>
    </DialogContent>
  );
}

export function DimeDialogHeader({
  ...props
}: Readonly<React.ComponentProps<typeof DialogHeader>>) {
  return <DialogHeader {...props} />;
}

/** Buttons sit side by side at every width, dismiss first, so the main action is always on the right; on a phone each takes half the row. */
export function DimeDialogFooter({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof DialogFooter>>) {
  return (
    <DialogFooter
      // Only the buttons split the row on a phone; summary text and button groups keep their own width.
      className={cn(
        'flex-row justify-end [&>:is(button,[data-slot=disabled-tooltip])]:flex-1 sm:[&>:is(button,[data-slot=disabled-tooltip])]:flex-none',
        className,
      )}
      {...props}
    />
  );
}

export function DimeDialogTitle({ ...props }: Readonly<React.ComponentProps<typeof DialogTitle>>) {
  return <DialogTitle {...props} />;
}

export function DimeDialogDescription({
  ...props
}: Readonly<React.ComponentProps<typeof DialogDescription>>) {
  return <DialogDescription {...props} />;
}
